import { ReactNode } from 'react';
import { Alert, Box, Center, Stack, Text } from '@mantine/core';
import { IconAlertCircle, IconClockExclamation, IconMailCheck } from '@tabler/icons-react';
import { Button } from 'src/components';
import { useResendExpiredInvite } from 'src/auth/hooks/useResendExpiredInvite';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

const StatusBadge = ({ background, children }: { background: string; children: ReactNode }) => (
  <Center w={64} h={64} bg={background} style={{ borderRadius: '50%' }}>
    {children}
  </Center>
);

const resendErrorMessage = (isCapped: boolean) =>
  isCapped
    ? 'You have already requested new invitations for this link. Please contact your administrator.'
    : 'We could not send a new invitation. Please contact your administrator.';

interface InviteResendCardProps {
  tenantId: string;
  token: string;
  footer?: ReactNode;
}

export const InviteResendCard = ({ tenantId, token, footer }: InviteResendCardProps) => {
  const { resendInvite, maskedEmail, isSending, isError, errorStatus } = useResendExpiredInvite();

  const handleResend = () => {
    void resendInvite({ tenantId, token }).catch(() => {
      // The hook's isError state drives the message; nothing to do here.
    });
  };

  if (maskedEmail) {
    return (
      <Stack gap="md" align="center">
        <StatusBadge background="green.1">
          <IconMailCheck size={32} color="var(--mantine-color-green-6)" />
        </StatusBadge>

        <Text fz="xl" fw={FONT_WEIGHTS.semibold} ta="center">
          Invitation sent
        </Text>

        <Text size="sm" c="gray.6" ta="center" style={{ textWrap: 'balance' }}>
          A new invitation is on its way to {maskedEmail}. Check your inbox.
        </Text>
      </Stack>
    );
  }

  const isCapped = errorStatus === 429;

  return (
    <Stack gap="md" align="center">
      <StatusBadge background="gray.1">
        <IconClockExclamation size={32} color="var(--mantine-color-gray-7)" />
      </StatusBadge>

      <Text fz="xl" fw={FONT_WEIGHTS.semibold} ta="center">
        This invitation link is no longer valid
      </Text>

      <Text size="sm" c="gray.6" ta="center" style={{ textWrap: 'balance' }}>
        It may have expired or already been used. We can email a new one to the same address.
      </Text>

      {!isCapped && (
        <Box w="100%" mt="xs">
          <Button onClick={handleResend} fullWidth loading={isSending}>
            Send me a new invitation
          </Button>
        </Box>
      )}

      {isError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" w="100%">
          {resendErrorMessage(isCapped)}
        </Alert>
      )}

      {footer}
    </Stack>
  );
};
